'use client';

/**
 * Right-side drawer that lists saved versions of a content row and lets
 * the owner peek / restore an earlier one.
 *
 * Backed by `/api/content/[id]/versions` (list) and
 * `/api/content/[id]/versions/[version]/restore` (write). The list
 * refreshes whenever the editor dispatches a `content-saved` event so
 * the timeline grows in real time.
 */

'use client';

import { useCallback, useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { History as HistoryIcon, RotateCcw, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VersionRow {
  version: number;
  byteSize: number;
  mimeType: string | null;
  command: string | null;
  createdAt: string;
  editedBy: { id: string; username: string | null; displayName: string | null } | null;
}

export interface VersionsPanelProps {
  contentId: string;
  onClose: () => void;
  onRestored: () => void | Promise<void>;
}

/**
 * Convert ms-since-now into a compact "5 min ago" / "2 hr ago" / date
 * string. Times within the last day round to relative; older falls
 * through to the local date.
 */
function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  const dt = Date.now() - t;
  const sec = Math.max(0, Math.floor(dt / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function commandLabel(command: string | null): string {
  if (!command) return 'manual edit';
  if (command.startsWith('restore_v')) return command.replace('restore_v', 'restored from v');
  return command.replace(/_/g, ' ');
}

export function VersionsPanel({ contentId, onClose, onRestored }: VersionsPanelProps) {
  const t = useT();
  const [rows, setRows] = useState<VersionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`/api/content/${contentId}/versions`, { cache: 'no-store' });
      if (!r.ok) {
        setError(r.status === 403 ? t('versions.readOnly') : t('versions.loadFailed'));
        setRows([]);
        return;
      }
      const d = (await r.json()) as { versions: VersionRow[] };
      setRows(d.versions);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [contentId, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live-refresh when a save lands.
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ id: string }>;
      if (ev.detail?.id !== contentId) return;
      void refresh();
    };
    window.addEventListener('content-saved', handler as EventListener);
    return () => window.removeEventListener('content-saved', handler as EventListener);
  }, [contentId, refresh]);

  // The confirmation is an inline row, not `window.confirm` (PORTAL-FILES-UX
  // audit A11, 2026-10-26): a native confirm is blocked in some embeds, reads
  // as a browser warning rather than ours, and cannot be translated. A failed
  // restore says what happened in words, never a bare status code.
  const [confirming, setConfirming] = useState<number | null>(null);
  const restore = async (version: number) => {
    setConfirming(null);
    setRestoring(version);
    try {
      const r = await fetch(`/api/content/${contentId}/versions/${version}/restore`, {
        method: 'POST',
      });
      if (!r.ok) {
        setError(r.status === 403 ? t('versions.restoreForbidden') : t('versions.restoreFailed'));
        return;
      }
      setError(null);
      await onRestored();
      await refresh();
    } catch {
      setError(t('versions.restoreFailed'));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <aside className="w-80 shrink-0 border-l border-border bg-card flex flex-col">
      <div className="h-12 shrink-0 flex items-center justify-between px-3 border-b border-border">
        <div className="flex items-center gap-2">
          <HistoryIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{t('versions.title')}</span>
        </div>
        <button
          onClick={onClose}
          className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-accent transition-colors"
          aria-label={t('versions.closeAria')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-2">
        {loading && rows === null && (
          <div className="text-xs text-muted-foreground p-3">{t('versions.loading')}</div>
        )}
        {error && <div className="text-xs text-destructive p-3">{error}</div>}
        {rows && rows.length === 0 && !error && (
          <div className="text-xs text-muted-foreground p-3">
            {t('versions.empty')}
          </div>
        )}
        {rows?.map((row, idx) => (
          <div
            key={row.version}
            className={cn(
              'group rounded-md p-2 text-xs hover:bg-muted/40 transition-colors',
              idx === 0 && 'bg-muted/30',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[11px] tabular-nums">v{row.version}</span>
                  {idx === 0 && (
                    <span className="text-[10px] uppercase tracking-wide text-emerald-500">{t('versions.latest')}</span>
                  )}
                </div>
                <div className="text-muted-foreground mt-0.5">
                  {relTime(row.createdAt)}
                  {row.editedBy?.username && (
                    <>
                      <span className="mx-1.5 opacity-50">·</span>
                      <span>@{row.editedBy.username}</span>
                    </>
                  )}
                </div>
                <div className="text-muted-foreground/80 text-[10px] mt-0.5">
                  {commandLabel(row.command)} · {row.byteSize.toLocaleString()} B
                </div>
              </div>
              {idx > 0 && confirming !== row.version && (
                <button
                  onClick={() => setConfirming(row.version)}
                  disabled={restoring !== null}
                  className="h-7 px-2 rounded-md flex items-center gap-1 text-[11px] hover:bg-accent border border-transparent hover:border-border transition-colors disabled:opacity-50 opacity-0 group-hover:opacity-100"
                  title={t('versions.restoreTitle', { version: row.version })}
                >
                  <RotateCcw className="h-3 w-3" />
                  {restoring === row.version ? t('versions.restoring') : t('versions.restore')}
                </button>
              )}
            </div>
            {confirming === row.version && (
              <div className="mt-2 rounded-md border border-border bg-background p-2" role="group" aria-label={t('versions.restoreTitle', { version: row.version })}>
                <div className="text-[11px] text-foreground">{t('versions.restoreConfirm', { version: row.version })}</div>
                <div className="mt-2 flex justify-end gap-1.5">
                  <button
                    onClick={() => setConfirming(null)}
                    className="h-7 px-2 rounded-md text-[11px] hover:bg-accent border border-border transition-colors"
                  >
                    {t('versions.cancel')}
                  </button>
                  <button
                    onClick={() => void restore(row.version)}
                    disabled={restoring !== null}
                    autoFocus
                    className="h-7 px-2 rounded-md flex items-center gap-1 text-[11px] bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" />
                    {t('versions.restore')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}
